/**
 * Database configuration.
 *
 * Builds Knex configuration based on DATABASE_URL.
 * Supports: SQLite, PostgreSQL, MySQL.
 */

import type { Knex } from 'knex'
import { join, dirname, isAbsolute } from 'path'
import { mkdirSync } from 'fs'
import { getConfig } from './env.js'
import { getProjectPath } from './paths.js'
import { sqlitePostProcess } from '@kernel/db/sqlite-compat.js'

/** Matches all in-memory URL variants: :memory:, file::memory:, sqlite::memory: */
const IN_MEMORY_RE = /^((file:|sqlite:)?:memory:?)$/

/**
 * Builds Knex configuration based on DATABASE_URL
 */
export function getDatabaseConfig(): Knex.Config {
  const url = getConfig().databaseUrl

  // Normalize all in-memory variants to canonical form
  if (IN_MEMORY_RE.test(url)) {
    return {
      client: 'better-sqlite3',
      connection: { filename: ':memory:' },
      useNullAsDefault: true,
      postProcessResponse: sqlitePostProcess,
      pool: { min: 0, max: 1 }
    }
  }

  // SQLite file-based
  if (url.startsWith('file:') || url.startsWith('sqlite:')) {
    let filename = url.replace(/^(file:|sqlite:)/, '')

    // Rutas relativas se resuelven desde getProjectPath()/data/
    if (!isAbsolute(filename)) {
      filename = join(getProjectPath(), 'data', filename)
    }
    // Asegurar que el directorio existe
    mkdirSync(dirname(filename), { recursive: true })
    return {
      client: 'better-sqlite3',
      connection: { filename },
      useNullAsDefault: true,
      postProcessResponse: sqlitePostProcess
    }
  }

  // PostgreSQL
  if (url.startsWith('postgresql://') || url.startsWith('postgres://')) {
    return {
      client: 'pg',
      connection: url,
      pool: { min: 2, max: 10 }
    }
  }

  // MySQL
  if (url.startsWith('mysql://')) {
    // Calcular offset del timezone actual (process.env.TZ ya aplicado)
    const offsetMinutes = new Date().getTimezoneOffset()
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60))
    const offsetMins = Math.abs(offsetMinutes % 60)
    const sign = offsetMinutes <= 0 ? '+' : '-'
    const tzOffset = `${sign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`

    return {
      client: 'mysql2',
      connection: {
        uri: url,
        timezone: tzOffset // mysql2 requiere formato "+HH:MM"
      },
      pool: { min: 2, max: 10 }
    }
  }

  throw new Error(`Unsupported database URL: ${url}`)
}

/**
 * Gets the database type from DATABASE_URL
 */
export function getDatabaseType(): 'sqlite' | 'postgresql' | 'mysql' {
  const url = getConfig().databaseUrl
  if (IN_MEMORY_RE.test(url) || url.startsWith('file:') || url.startsWith('sqlite:')) return 'sqlite'
  if (url.startsWith('postgresql://') || url.startsWith('postgres://')) return 'postgresql'
  if (url.startsWith('mysql://')) return 'mysql'
  return 'sqlite'
}

/**
 * Gets the database path/URL (credentials masked for display)
 */
export function getDatabasePath(): string {
  const url = getConfig().databaseUrl
  if (IN_MEMORY_RE.test(url)) return ':memory:'
  if (url.startsWith('file:') || url.startsWith('sqlite:')) {
    let filename = url.replace(/^(file:|sqlite:)/, '')
    if (!isAbsolute(filename)) {
      filename = join(getProjectPath(), 'data', filename)
    }
    return filename
  }
  // Para PostgreSQL/MySQL, ocultar credenciales
  try {
    const parsed = new URL(url)
    parsed.password = '***'
    return parsed.toString()
  } catch {
    return url.replace(/:\/\/[^@]+@/, '://***@')
  }
}
